import { useCallback, useState } from 'react';

export type DropdownState = {
  [key: number]: boolean;
};

export const useDropdownManager = () => {
  const [dropdownStates, setDropdownStates] = useState<DropdownState>({});

  const toggleDropdown = useCallback(
    (index: number) => {
      setDropdownStates(prev => ({
        ...prev,
        [index]: !prev[index],
      }));
    },
    [setDropdownStates]
  );

  const updateDropdownStates = useCallback(
    (
      newDropdownState: DropdownState | ((prev: DropdownState) => DropdownState)
    ) => {
      setDropdownStates(prevState =>
        typeof newDropdownState === 'function'
          ? newDropdownState(prevState)
          : newDropdownState
      );
    },
    [setDropdownStates]
  );

  const setLastDropdownOpen = useCallback(
    (newModuleIndex: number) => {
      setDropdownStates(prev => {
        const updatedState = Object.keys(prev).reduce((acc, key) => {
          acc[Number(key)] = false;
          return acc;
        }, {} as DropdownState);

        return { ...updatedState, [newModuleIndex]: true };
      });
    },
    [setDropdownStates]
  );

  const updateDropdownsOrder = useCallback(
    (oldIndex: number, newIndex: number) => {
      return setDropdownStates(prev => {
        const movedDropdownStates = {
          [newIndex]: prev[oldIndex],
          [oldIndex]: prev[newIndex],
        };

        return { ...prev, ...movedDropdownStates };
      });
    },
    [setDropdownStates]
  );

  const openAllDropdowns = useCallback(() => {
    setDropdownStates(prev => {
      const allOpenStates = Object.keys(prev).reduce((acc, key) => {
        acc[Number(key)] = true;
        return acc;
      }, {} as DropdownState);

      return allOpenStates;
    });
  }, [setDropdownStates]);

  return {
    dropdownStates,
    toggleDropdown,
    updateDropdownStates,
    updateDropdownsOrder,
    openAllDropdowns,
    setLastDropdownOpen,
  };
};
